import useAppQuery from 'hooks/useAppQuery';
import { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { config } from 'variables/config';

import MuiBarCharts from 'components/charts/MUIBarCharts';
import MuiLineChart from 'components/charts/MuiLineChart';
import BasicPie from 'components/charts/PieChart';
import MuiSelect from 'components/MuiSelect';
import { useAuthUser } from 'react-auth-kit';
import { useTranslation } from 'react-i18next';
import { capitalyse, castToRequestParams, formatNumber } from 'utils';

// ============================================
// HELPER FUNCTIONS
// ============================================

// Normalize gender labels to standard categories
const buildGenderLabel = (label) => {
  if (label === 'Male' || label === 'Masculin' || label === 'M') return 'Masculin';
  if (label === 'Female' || label === 'Feminin' || label === 'F') return 'Feminin';
  return 'ND';
};

const TabContent2 = ({ regionId, regionParams }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuthUser();

  const { t } = useTranslation('dashboard');

  // ============================================
  // STATE MANAGEMENT
  // ============================================
  const [departement, setDepartement] = useState(null);
  // console.log("🚀 ~ TabContent2 ~ departement:", departement)
  const [arrondissement, setArrondissement] = useState(null);

  // Filter parameters for API requests
  const [params, setParams] = useState({
    enterpriseId: null,
    start: null,
    end: null,
    visaExpired: null,
    regionId:
      regionId || location?.state?.region_id != undefined || location?.state?.region_id != null
        ? location?.state?.region_id
        : null,
    departmentId: null,
    arrondissementId: null,
    page: null,
    limit: null,
    fromArchive: null,
  });

  // ============================================
  // EFFECTS - Update params when region changes
  // ============================================
  useEffect(() => {
    setParams({ ...params, regionId: regionId });
  }, [regionId]);

  // ============================================
  // DATA FETCHING HOOKS
  // ============================================

  // Memoized query strings for params-dependent stat endpoints
  const paramsQueryString = useMemo(() => castToRequestParams(params), [params]);

  // Fetch regions list
  const { data: regions } = useAppQuery(`${config.app.api_url}/regions`);

  // Fetch departments by region id
  const { data: departements } = useAppQuery(
    regionId ? `${config.app.api_url}/regions/${regionId}/departments` : null
  );

  // Fetch arrondissements by department id
  const { data: arrondisements } = useAppQuery(
    departement ? `${config.app.api_url}/departments/${departement}/arrondissements` : null
  );

  // --- ENTERPRISE STATISTICS ---

  // Global enterprise stats with regional filtering
  const { data: stats } = useAppQuery(
    `${config.app.api_url}/enterprises/stats-details?regionId=${regionId ? regionId : ''}${params?.departmentId ? `&departmentId=${params?.departmentId}` : ''}${params?.arrondissementId ? `&arrondissementId=${params?.arrondissementId}` : ''}`
  );

  // Terrain enterprise stats (excluding archived)
  const { data: statsT } = useAppQuery(
    `${config.app.api_url}/enterprises/stats-details?fromArchive=false&${paramsQueryString}`
  );

  // --- EMPLOYEE/EMPLOYER STATISTICS ---

  // Global employee stats
  const { data: statsE } = useAppQuery(
    `${config.app.api_url}/employers/stats-details?${paramsQueryString}`
  );

  // Terrain employee stats (excluding archived)
  const { data: statsTE } = useAppQuery(
    `${config.app.api_url}/employers/stats-details?fromArchive=false&${paramsQueryString}`
  );

  // Employee stats by nationality - Global
  const { data: statsNat } = useAppQuery(
    `${config.app.api_url}/employers/stats-by-nationality?${paramsQueryString}`
  );

  // Employee stats by nationality - Terrain
  const { data: statsNatTE } = useAppQuery(
    `${config.app.api_url}/employers/stats-by-nationality?fromArchive=false&${paramsQueryString}`
  );

  // Employee stats by permit date
  const { data: statsPerms } = useAppQuery(
    `${config.app.api_url}/employers/stats-by-permit-date?${paramsQueryString}`
  );

  // Employee stats by salary range
  const { data: statsSalary } = useAppQuery(
    `${config.app.api_url}/employers/stats-by-salary?${paramsQueryString}`
  );

  // Employee stats by gender - Global
  const { data: sexStats } = useAppQuery(
    `${config.app.api_url}/employers/stats-by-sexe?${paramsQueryString}`
  );

  // Employee stats by gender - Terrain
  const { data: sexStatsTE } = useAppQuery(
    `${config.app.api_url}/employers/stats-by-sexe?fromArchive=false&${paramsQueryString}`
  );

  // --- REGIONAL STATISTICS ---

  // Employee stats across national regions
  const { data: NRStats } = useAppQuery(`${config.app.api_url}/employers/stats-national-regions`);

  // Employee stats by departments within a region
  const { data: DRStats } = useAppQuery(
    regionId
      ? `${config.app.api_url}/employers/stats-departments-by-region${regionId && regionId !== 'National' ? '?regionId=' + regionId : ''}`
      : null
  );

  // Enterprise stats across national regions
  const { data: ENRStats } = useAppQuery(
    `${config.app.api_url}/enterprises/stats-national-regions`
  );

  // Enterprise stats by departments within a region
  const { data: ENDRStats } = useAppQuery(
    regionId
      ? `${config.app.api_url}/enterprises/stats-departments-by-region${regionId && regionId !== 'National' ? '?regionId=' + regionId : ''}`
      : null
  );

  // ============================================
  // MEMOIZED DATA PROCESSING
  // ============================================

  // Memoized nationality stats for global employees
  // Processes data to show top 4 nationalities + "Autres" for remaining
  const statsNatProcessed = useMemo(() => {
    const sorted = statsNat?.data?.sort((a, b) => b.number - a.number) || [];
    const top4 = sorted
      .slice(0, 4)
      .map((a, index) => {
        if (a?.percent > 1) {
          return {
            id: index,
            nb: parseInt(a.number),
            title: capitalyse(a?.label),
            value: parseInt(a.number),
            label: a?.label?.toLowerCase(),
          };
        }
      })
      .filter(Boolean);

    const others = sorted.slice(4).reduce((acc, curr) => acc + parseInt(curr.number), 0);
    if (others > 0) {
      top4.push({
        id: 4,
        nb: others,
        title: 'Autres',
        value: others,
        label: 'autres',
      });
    }

    return {
      cardItems: top4.map(({ id, nb, title }) => ({ id, nb, title })),
      pieData: top4.map(({ id, value, label }) => ({ id, value, label })),
      total: statsNat?.data?.reduce((acc, curr) => acc + parseInt(curr.number), 0) || 0,
    };
  }, [statsNat?.data]);

  // Memoized nationality stats for terrain employees
  // Processes data to show top 4 nationalities + "Autres" for remaining
  const statsNatTEProcessed = useMemo(() => {
    const sorted = statsNatTE?.data?.sort((a, b) => b.number - a.number) || [];
    const top4 = sorted
      .slice(0, 4)
      .map((a, index) => {
        if (a?.percent > 1) {
          return {
            id: index,
            nb: parseInt(a.number),
            title: capitalyse(a?.label),
            value: parseInt(a.number),
            label: a?.label?.toLowerCase(),
          };
        }
      })
      .filter(Boolean);

    const others = sorted.slice(4).reduce((acc, curr) => acc + parseInt(curr.number), 0);
    if (others > 0) {
      top4.push({
        id: 4,
        nb: others,
        title: 'Autres',
        value: others,
        label: 'autres',
      });
    }

    return {
      cardItems: top4.map(({ id, nb, title }) => ({ id, nb, title })),
      pieData: top4.map(({ id, value, label }) => ({ id, value, label })),
      total: statsNatTE?.data?.reduce((acc, curr) => acc + parseInt(curr.number), 0) || 0,
    };
  }, [statsNatTE?.data]);

  // Memoized gender stats for global employees
  // Groups data into 3 categories: Masculin, Feminin, and Autres
  const sexStatsProcessed = useMemo(() => {
    if (!sexStats?.data) return { cardItems: [], pieData: [] };

    // Aggregate data by normalized gender labels
    const genderMap = { Masculin: 0, Feminin: 0, ND: 0 };

    sexStats.data.forEach((e) => {
      const normalizedLabel = buildGenderLabel(e?.label);
      genderMap[normalizedLabel] += parseInt(e?.number) || 0;
    });

    // Convert to array format for cards and pie chart
    const cardItems = Object.entries(genderMap)
      .filter(([_, count]) => count > 0)
      .map(([label, count], index) => ({
        id: index,
        nb: count,
        title: label,
        type: 'green-500',
      }));

    const pieData = Object.entries(genderMap)
      .filter(([_, count]) => count > 0)
      .map(([label, count], index) => ({
        id: index,
        value: count,
        label: label,
      }));

    return { cardItems, pieData };
  }, [sexStats?.data]);

  // Memoized gender stats for terrain employees
  // Groups data into 3 categories: Masculin, Feminin, and Autres
  const sexStatsTEProcessed = useMemo(() => {
    if (!sexStatsTE?.data) return { cardItems: [], pieData: [] };

    // Aggregate data by normalized gender labels
    const genderMap = { Masculin: 0, Feminin: 0, ND: 0 };

    sexStatsTE.data.forEach((e) => {
      const normalizedLabel = buildGenderLabel(e?.label);
      genderMap[normalizedLabel] += parseInt(e?.number) || 0;
    });

    // Convert to array format for cards and pie chart
    const cardItems = Object.entries(genderMap)
      .filter(([_, count]) => count > 0)
      .map(([label, count], index) => ({
        id: index,
        nb: count,
        title: label,
        type: 'green-500',
      }));

    const pieData = Object.entries(genderMap)
      .filter(([_, count]) => count > 0)
      .map(([label, count], index) => ({
        id: index,
        value: count,
        label: label,
      }));

    return { cardItems, pieData };
  }, [sexStatsTE?.data]);

  // Memoized bar chart data for enterprise department distribution
  const ENDRStatsProcessed = useMemo(() => {
    if (!ENDRStats?.data || ENDRStats.data.length === 0) return null;

    const sorted = ENDRStats.data.filter((s) => s?.percent > 0).sort((a, b) => b.number - a.number);

    return {
      data: sorted.map((a) => parseInt(a.number)),
      xLabel: sorted.map((a) => a.label),
    };
  }, [ENDRStats?.data]);

  // Memoized line chart data for permit evolution by year
  const statsPermsProcessed = useMemo(() => {
    if (!statsPerms?.data) return null;

    const validData = statsPerms.data.filter((s) => s?.year != null);

    return {
      xAxis: validData.map((e) => e?.year),
      data: validData.map((e) => e?.number),
    };
  }, [statsPerms?.data]);

  // Memoized bar chart data for salary distribution
  const statsSalaryProcessed = useMemo(() => {
    if (!statsSalary?.data || statsSalary.data.length === 0) return null;

    return {
      data: statsSalary.data.map((d) => d?.number),
      xLabel: statsSalary.data.map((a) => a?.range),
    };
  }, [statsSalary?.data]);

  // Memoized bar chart data for top 10 nationalities
  const statsNatTop10Processed = useMemo(() => {
    if (!statsNat?.data || statsNat.data.length === 0) return null;

    const filtered = statsNat.data
      .filter((s) => s?.percent > 1)
      .sort((a, b) => b.number - a.number)
      .slice(0, 10);

    return {
      data: filtered.map((a) => parseInt(a.number)),
      xLabel: filtered.map((a) => a.label),
    };
  }, [statsNat?.data]);

  // ============================================
  // UI COMPONENTS
  // ============================================

  // Item component - Displays individual stat item with title and number
  const Item = ({ title, nb, type, onClick }) => {
    return (
      <div
        className=" transition-all duration-300 cursor-pointer hover:shadow-md  hover:font-bold  hover:bg-green-50 rounded-xl  hover:text-[16px] "
        onClick={onClick}
      >
        {<strong class=" text-gray-700">{title || 'Non defini'}</strong>}

        <h6 class={`font-bold  text-${type || 'blue-500'}`}>{nb}</h6>
      </div>
    );
  };

  // Card component - Container for multiple stat items
  const Card = ({ items, type, direction, onClick }) => {
    return (
      <>
        <div class={` flex flex-${direction || 'col'} flex-wrap gap-2    p-4`}>
          {items?.map((item, index) => {
            return (
              item?.nb != null &&
              item?.nb != undefined && (
                <Item
                  onClick={item?.onClick}
                  key={index}
                  nb={formatNumber(item?.nb)}
                  type={item?.type}
                  title={item?.title}
                />
              )
            );
          })}
        </div>
      </>
    );
  };

  const items = [];

  // ============================================
  // RENDER
  // ============================================
  return (
    <div>
      {/* {(loadingR || loadingD || loadingA || loading || loadingE || loadingNE || loadingSal || loadingSex) && (
        <PreloaderSpinner />
      )} */}

      {/* ========================================
          HEADER - Region/Department/Arrondissement Filters
          ======================================== */}
      <div
        className="flex gap-2 justify-center overflow-auto p-2 pb-2 mb-2 bg-white
         justify-between items-center  rounded "
      >
        <h1 className="text-5xl text-primary font-bold text-center leading-tight">
          {`${regionParams?.name || 'National'} >`}
        </h1>
        <div className="flex gap-2">
          {departements?.data && (
            <MuiSelect
              size={'small'}
              label={'Département'}
              options={departements?.data}
              emptyLabel={'Régional'}
              // className={"bg-white"}
              value={departement}
              defaultFormat={true}
              onChange={(e) => {
                setDepartement(e.target.value);
                setParams({ ...params, departmentId: e.target.value });
                setArrondissement('');
              }}
              wrapperClasses={' filter-item anime slideLeft'}
            />
          )}

          {/* Filtre arrondissement */}
          {arrondisements?.data && (
            <MuiSelect
              size={'small'}
              label={'Arrondissement'}
              emptyLabel={'Départemental'}
              options={arrondisements?.data}
              value={arrondissement?.id}
              defaultFormat={true}
              onChange={(e) => {
                setArrondissement(e.target.value);
                setParams({ ...params, arrondissementId: e.target.value });
              }}
              wrapperClasses={' filter-item anime slideRight'}
            />
          )}
        </div>
      </div>

      {/* ========================================
          ENTERPRISE STATISTICS - GLOBAL
          All enterprises including archived
          ======================================== */}
      <div className="grid grid-cols-1 sm:grid-cols-1  gap-3">
        {/* <TabBar /> */}
        {/* <div className="flex  flex-col gap-4 content-center shadow"> */}
        <div
          class=" flex flex-col flex-nowrap gap-4 content-center bg-blur-green 
         justify-center items-center  rounded py-0   
        "
        >
          <div className="w-full flex flex-col items-center bg-blur-white p-2 rounded-lg shadow px-5">
            <strong className="text-xl">{t('stat_global_title')}</strong>

            <span
              className="text-blue-700 italic cursor-pointer font-bold hover:shadow-md  hover:font-bold  hover:bg-green-50 rounded-xl  hover:text-[16px]"
              onClick={() => {
                navigate('/admin/enterprises', { state: { ...params, regionId: regionId } });
              }}
            >
              Total : {formatNumber(stats?.data?.total)}
            </span>
            {/* <Item nb={stats?.data?.total} type={'blue-500'} title={"Total des entreprises"} /> */}
          </div>
          <div class="w-full  flex gap-2 flex-wrap justify-center items-center overflow-auto ">
            <div
              className="h-[450px]  border border-gray-500 rounded-lg shadow-md overflow-auto
            bg-white
            "
            >
              <Card
                items={[
                  // {title : "Total des entreprises", nb : stats?.data?.total, type : 'blue-500'},
                  {
                    title: t('in_regle'),
                    nb: stats?.data?.regular,
                    type: 'green-500',
                    onClick: () =>
                      navigate('/admin/enterprises', {
                        state: { ...params, isRegular: true, regionId: regionId },
                      }),
                  },
                  {
                    title: t('in_irregulier'),
                    nb: stats?.data?.irregular,
                    type: 'red-500',
                    onClick: () =>
                      navigate('/admin/enterprises', {
                        state: { ...params, isRegular: false, regionId: regionId },
                      }),
                  },
                ]}
              />
              <BasicPie
                data={
                  stats?.data?.total
                    ? [
                        {
                          id: 1,
                          label: t('in_regle'),
                          value: stats?.data?.regular,
                          color: '#50C878',
                        },
                        {
                          id: 2,
                          label: t('in_irregulier'),
                          value: stats?.data?.irregular,
                          color: '#FF1493',
                        },
                      ]
                    : []
                }
                colors={['#FF1493', 'blue', '#50C878']}
                // width={400}
                count={stats?.data?.total}
              />
            </div>
            <div className=" h-[450px]   border border-gray-500 rounded-lg shadow-md bg-white">
              <Card
                items={[
                  {
                    title: t('collect_only'),
                    nb: stats?.data?.collected,
                    type: 'green-500',
                    onClick: () =>
                      navigate('/admin/enterprises', {
                        state: { ...params, fromArchive: false, regionId: regionId },
                      }),
                  },
                  {
                    title: t('no_collect_with'),
                    nb: stats?.data?.fromArchive,
                    type: 'gray-500',
                    onClick: () =>
                      navigate('/admin/enterprises', {
                        state: { ...params, fromArchive: true, regionId: regionId },
                      }),
                  },
                ]}
              />
              <BasicPie
                data={
                  stats?.data?.total
                    ? [
                        {
                          id: 1,
                          label: t('collect_only'),
                          value: stats?.data?.collected,
                          color: '#50C878',
                        },
                        {
                          id: 2,
                          label: t('no_collect_only'),
                          value: stats?.data?.fromArchive,
                          color: '#FF1493',
                        },
                      ]
                    : []
                }
                colors={['#FF1493', 'blue', '#50C878', 'orange', 'gray']}
                // width={400}
                count={stats?.data?.total}
              />
            </div>

            <div className=" h-[450px]  border border-gray-500 rounded-lg shadow-md bg-white">
              {/* <Card title={"Entreprises en règle"} nb={stats?.data?.regular} type={'green-500'} /> */}
              <Card
                items={[
                  // {title : "Total des entreprises", nb : stats?.data?.total, type : 'blue-500'},
                  // {
                  //   title: "Visitées | Par repérage", nb: formatNumber(stats?.data?.visited) + ' | '+ formatNumber(stats?.data?.visitedSpotting) +
                  //   ' = '+ formatNumber(stats?.data?.visited + stats?.data?.visitedSpotting), type: 'green-500',
                  //   onClick: () => navigate('/admin/enterprises', { state: {...params, located: true, regionId: regionId } })
                  // },

                  {
                    title: t('visited'),
                    nb: formatNumber(stats?.data?.visited),
                    type: 'green-500',
                    onClick: () =>
                      navigate('/admin/enterprises', {
                        state: { ...params, located: true, regionId: regionId },
                      }),
                  },

                  {
                    title: t('not_visited'),
                    nb: stats?.data?.nonVisited,
                    type: 'gray-500',
                    onClick: () =>
                      navigate('/admin/enterprises', {
                        state: { ...params, located: false, regionId: regionId },
                      }),
                  },
                ]}
              />
              <BasicPie
                data={
                  stats?.data?.total
                    ? [
                        {
                          id: 1,
                          label: t('visited'),
                          value: stats?.data?.visited,
                          color: '#50C878',
                        },
                        {
                          id: 2,
                          label: t('not_visited'),
                          value: stats?.data?.nonVisited,
                          color: '#FF1493',
                        },
                      ]
                    : []
                }
                colors={['#FF1493', 'blue', '#50C878', 'orange', 'gray']}
                // width={400}
                count={stats?.data?.total}
              />
            </div>

            <div className=" h-[450px]  border border-gray-500 rounded-lg shadow-md bg-white">
              {/* <Card title={"Entreprises en règle"} nb={stats?.data?.regular} type={'green-500'} /> */}
              <Card
                items={[
                  // {title : "Total des entreprises", nb : stats?.data?.total, type : 'blue-500'},
                  {
                    title: t('missioned'),
                    nb: stats?.data?.missioned,
                    type: 'green-500',
                    onClick: () =>
                      navigate('/admin/enterprises', {
                        state: { ...params, mission: { id: -2 }, regionId: regionId },
                      }),
                  },
                  {
                    title: t('not_missioned'),
                    nb: stats?.data?.nonMissioned,
                    type: 'gray-500',
                    onClick: () =>
                      navigate('/admin/enterprises', {
                        state: { ...params, mission: { id: -1 }, regionId: regionId },
                      }),
                  },
                ]}
              />
              <BasicPie
                data={
                  stats?.data?.total
                    ? [
                        {
                          id: 1,
                          label: t('missionned'),
                          value: stats?.data?.missioned,
                          color: '#50C878',
                        },
                        {
                          id: 2,
                          label: t('not_missioned'),
                          value: stats?.data?.nonMissioned,
                          color: '#FF1493',
                        },
                      ]
                    : []
                }
                colors={['#FF1493', 'blue', '#50C878', 'orange', 'gray']}
                // width={400}
                count={stats?.data?.total}
              />
            </div>
          </div>
        </div>

        {/* ========================================
            ENTERPRISE STATISTICS - TERRAIN
            Only enterprises from field collection (excluding archived)
            ======================================== */}
        <div
          class=" flex flex-col flex-nowrap gap-4 content-center bg-blur-green 
         justify-center items-center  rounded py-0   
        "
        >
          <div className="w-full flex flex-col items-center bg-blur-white p-2 rounded-lg shadow px-5">
            <strong className="text-xl">{t('stat_terrain_title')}</strong>

            <span
              className="text-blue-700 italic cursor-pointer font-bold hover:shadow-md  hover:font-bold  hover:bg-green-50 rounded-xl  hover:text-[16px]"
              onClick={() => {
                navigate('/admin/enterprises', {
                  state: { ...params, fromArchive: false, regionId: regionId },
                });
              }}
            >
              Total : {formatNumber(statsT?.data?.total)}
            </span>
            {/* <Item nb={stats?.data?.total} type={'blue-500'} title={"Total des entreprises"} /> */}
          </div>
          <div class="w-full  flex gap-2 flex-wrap justify-center items-center overflow-auto ">
            <div
              className="h-[450px]  border border-gray-500 rounded-lg shadow-md overflow-auto
            bg-white
            "
            >
              <Card
                items={[
                  // {title : "Total des entreprises", nb : stats?.data?.total, type : 'blue-500'},
                  {
                    title: t('in_regle'),
                    nb: statsT?.data?.regular,
                    type: 'green-500',
                    onClick: () =>
                      navigate('/admin/enterprises', {
                        state: {
                          ...params,
                          isRegular: true,
                          fromArchive: false,
                          regionId: regionId,
                        },
                      }),
                  },
                  {
                    title: t('in_irregulier'),
                    nb: statsT?.data?.irregular,
                    type: 'red-500',
                    onClick: () =>
                      navigate('/admin/enterprises', {
                        state: {
                          ...params,
                          isRegular: false,
                          fromArchive: false,
                          regionId: regionId,
                        },
                      }),
                  },
                ]}
              />
              <BasicPie
                data={
                  statsT?.data?.total
                    ? [
                        {
                          id: 1,
                          label: t('in_regle'),
                          value: statsT?.data?.regular,
                          color: '#50C878',
                        },
                        {
                          id: 2,
                          label: t('in_irregulier'),
                          value: statsT?.data?.irregular,
                          color: '#FF1493',
                        },
                      ]
                    : []
                }
                colors={['#FF1493', 'blue', '#50C878']}
                // width={400}
                count={statsT?.data?.total}
              />
            </div>

            <div className=" h-[450px]  border border-gray-500 rounded-lg shadow-md bg-white">
              {/* <Card title={"Entreprises en règle"} nb={stats?.data?.regular} type={'green-500'} /> */}
              <Card
                items={[
                  {
                    title: t('visited'),
                    nb: formatNumber(statsT?.data?.visited),
                    type: 'green-500',
                    onClick: () =>
                      navigate('/admin/enterprises', {
                        state: { ...params, located: true, fromArchive: false, regionId: regionId },
                      }),
                  },

                  {
                    title: t('not_visited'),
                    nb: statsT?.data?.nonVisited,
                    type: 'gray-500',
                    onClick: () =>
                      navigate('/admin/enterprises', {
                        state: {
                          ...params,
                          located: false,
                          fromArchive: false,
                          regionId: regionId,
                        },
                      }),
                  },
                ]}
              />
              <BasicPie
                data={
                  statsT?.data?.total
                    ? [
                        {
                          id: 1,
                          label: t('visited'),
                          value: statsT?.data?.visited,
                          color: '#50C878',
                        },
                        {
                          id: 2,
                          label: t('not_visited'),
                          value: statsT?.data?.nonVisited,
                          color: '#FF1493',
                        },
                      ]
                    : []
                }
                colors={['#FF1493', 'blue', '#50C878', 'orange', 'gray']}
                // width={400}
                count={statsT?.data?.total}
              />
            </div>

            <div className=" h-[450px]  border border-gray-500 rounded-lg shadow-md bg-white">
              {/* <Card title={"Entreprises en règle"} nb={stats?.data?.regular} type={'green-500'} /> */}
              <Card
                items={[
                  // {title : "Total des entreprises", nb : stats?.data?.total, type : 'blue-500'},
                  {
                    title: t('missioned'),
                    nb: statsT?.data?.missioned,
                    type: 'green-500',
                    onClick: () =>
                      navigate('/admin/enterprises', {
                        state: {
                          ...params,
                          mission: { id: -2 },
                          fromArchive: false,
                          regionId: regionId,
                        },
                      }),
                  },
                  {
                    title: t('not_missioned'),
                    nb: statsT?.data?.nonMissioned,
                    type: 'gray-500',
                    onClick: () =>
                      navigate('/admin/enterprises', {
                        state: {
                          ...params,
                          mission: { id: -1 },
                          fromArchive: false,
                          regionId: regionId,
                        },
                      }),
                  },
                ]}
              />
              <BasicPie
                data={
                  statsT?.data?.total
                    ? [
                        {
                          id: 1,
                          label: t('missioned'),
                          value: statsT?.data?.missioned,
                          color: '#50C878',
                        },
                        {
                          id: 2,
                          label: t('not_missioned'),
                          value: statsT?.data?.nonMissioned,
                          color: '#FF1493',
                        },
                      ]
                    : []
                }
                colors={['#FF1493', 'blue', '#50C878', 'orange', 'gray']}
                // width={400}
                count={stats?.data?.total}
              />
            </div>
          </div>
        </div>

        {/* ========================================
            EMPLOYEE/EMPLOYER STATISTICS - GLOBAL
            All employees including archived
            ======================================== */}
        <div className="flex flex-col  gap-4   justify-center items-center  rounded py-0 my-0">
          <div className="flex flex-col items-center bg-blur-white p-3 px-5 rounded-lg w-full ">
            <strong className="text-xl">{t('stat_global_exp')}</strong>
            <span
              className="text-blue-700 italic cursor-pointer font-bold hover:shadow-md  hover:font-bold  hover:bg-green-50 rounded-xl  hover:text-[16px]"
              onClick={() => {
                navigate('/admin/employes', { state: { ...params, regionId: regionId } });
              }}
            >
              Total : {formatNumber(statsE?.data?.total)}
            </span>
            {/* <Item nb={stats?.data?.total} type={'blue-500'} title={"Total des entreprises"} /> */}
          </div>

          <div class="w-full flex gap-3 justify-center items-center ">
            {/* Pie chart: Regular vs Irregular employees */}
            <div
              className="h-[450px]  border border-gray-500 rounded-lg shadow-md overflow-auto
            bg-white
            "
            >
              <Card
                items={[
                  // {title : "Total des entreprises", nb : stats?.data?.total, type : 'blue-500'},
                  {
                    title: t('in_regle'),
                    nb: statsE?.data?.regular,
                    type: 'green-500',
                    onClick: () =>
                      navigate('/admin/employes', {
                        state: { ...params, visaExpired: false, regionId: regionId },
                      }),
                  },
                  {
                    title: t('in_irregulier'),
                    nb: statsE?.data?.irregular,
                    type: 'red-500',
                    onClick: () =>
                      navigate('/admin/employes', {
                        state: { ...params, visaExpired: true, regionId: regionId },
                      }),
                  },
                ]}
              />

              <BasicPie
                data={
                  statsE?.data?.total
                    ? [
                        {
                          id: 1,
                          label: t('in_regle'),
                          value: statsE?.data?.regular,
                          color: '#50C878',
                        },
                        {
                          id: 2,
                          label: t('in_irregulier'),
                          value: statsE?.data?.irregular,
                          color: '#FF1493',
                        },
                      ]
                    : []
                }
                colors={['#FF1493', 'blue', '#50C878']}
                // width={400}
                count={statsE?.data?.total}
              />
            </div>

            {/* Pie chart: Gender distribution */}
            <div
              className="h-[450px]  border border-gray-500 rounded-lg shadow-md overflow-auto
             bg-white "
            >
              <Card items={sexStatsProcessed.cardItems} />

              <BasicPie data={sexStatsProcessed.pieData} count={statsE?.data?.total} />
            </div>

            {/* Pie chart: Top 4 nationalities + Others (Global) */}
            <div
              className="h-[450px]  border border-gray-500 rounded-lg shadow-md overflow-auto
             bg-white
            "
            >
              <Card direction={'row'} items={statsNatProcessed.cardItems} />

              <BasicPie
                data={statsNatProcessed.pieData}
                count={statsNatProcessed.total}
                legendDirection={'row'}
              />
            </div>

            {/* {region == 'National' ?
            <div className="h-[450px] w-[400px]  border border-gray-500 rounded-lg shadow-md overflow-auto">
              <Card
                direction={'row'}

                items={ENRStats?.data
                  ?.sort((a, b) => b.number - a.number)
                  ?.map((a, index) => {
                    if (true || a?.percent > 1) { return { id: index, nb: parseInt(a.number), title: a?.label } }
                  })} />


              <BasicPie data={ENRStats?.data
                ?.sort((a, b) => b.number - a.number)
                ?.map((a, index) => {
                  if (true || a?.percent > 1) { return { id: index, value: parseInt(a.number), label: a?.label?.toLowerCase() } }
                })}
                count={statsE?.data?.total}
                legendDirection={"row"}

              />
            </div>
            :
            <div className="h-[450px] w-[400px]  border border-gray-500 rounded-lg shadow-md overflow-auto">
              <Card
                direction={'row'}

                items={ENDRStats?.data
                  ?.sort((a, b) => b.number - a.number)
                  ?.map((a, index) => {
                    if (true || a?.percent > 1) { return { id: index, nb: parseInt(a.number), title: a?.label } }
                  })} />


              <BasicPie data={ENDRStats?.data
                ?.sort((a, b) => b.number - a.number)
                ?.map((a, index) => {
                  if (true || a?.percent > 1) { return { id: index, value: parseInt(a.number), label: a?.label?.toLowerCase() } }
                })}
                count={statsE?.data?.total}
                legendDirection={"row"}

              />
            </div>
            
          } */}
          </div>
        </div>

        {/* ========================================
            EMPLOYEE/EMPLOYER STATISTICS - TERRAIN
            Only employees from field collection (excluding archived)
            ======================================== */}
        <div className="flex flex-col  gap-4   justify-center items-center  rounded py-0 my-0">
          <div className="flex flex-col items-center bg-blur-white p-3 px-5 rounded-lg w-full ">
            <strong className="text-xl">{t('stat_terrain_exp')}</strong>
            <span
              className="text-blue-700 italic cursor-pointer font-bold hover:shadow-md  hover:font-bold  hover:bg-green-50 rounded-xl  hover:text-[16px]"
              onClick={() => {
                navigate('/admin/employes', {
                  state: { ...params, fromArchive: false, regionId: regionId },
                });
              }}
              // onClick: () => navigate('/admin/employes', { state: {...params, visaExpired: true, fromArchive : false, regionId: regionId } })
            >
              Total : {formatNumber(statsTE?.data?.total)}
            </span>
            {/* <Item nb={stats?.data?.total} type={'blue-500'} title={"Total des entreprises"} /> */}
          </div>

          <div class="w-full flex gap-3 justify-center items-center ">
            {/* Pie chart: Regular vs Irregular employees (Terrain) */}
            <div
              className="h-[450px]  border border-gray-500 rounded-lg shadow-md overflow-auto
  bg-white
  "
            >
              <Card
                items={[
                  // {title : "Total des entreprises", nb : stats?.data?.total, type : 'blue-500'},
                  {
                    title: t('in_regle'),
                    nb: statsTE?.data?.regular,
                    type: 'green-500',
                    onClick: () =>
                      navigate('/admin/employes', {
                        state: {
                          ...params,
                          visaExpired: false,
                          fromArchive: false,
                          regionId: regionId,
                        },
                      }),
                  },
                  {
                    title: t('in_irregulier'),
                    nb: statsTE?.data?.irregular,
                    type: 'red-500',
                    onClick: () =>
                      navigate('/admin/employes', {
                        state: {
                          ...params,
                          visaExpired: true,
                          fromArchive: false,
                          regionId: regionId,
                        },
                      }),
                  },
                ]}
              />

              <BasicPie
                data={
                  statsTE?.data?.total
                    ? [
                        {
                          id: 1,
                          label: t('in_regle'),
                          value: statsTE?.data?.regular,
                          color: '#50C878',
                        },
                        {
                          id: 2,
                          label: t('in_irregulier'),
                          value: statsTE?.data?.irregular,
                          color: '#FF1493',
                        },
                      ]
                    : []
                }
                colors={['#FF1493', 'blue', '#50C878']}
                // width={400}
                count={statsTE?.data?.total}
              />
            </div>

            {/* Pie chart: Gender distribution (Terrain) */}
            <div className="h-[450px]  border border-gray-500 rounded-lg shadow-md overflow-auto bg-white ">
              <Card items={sexStatsTEProcessed.cardItems} />

              <BasicPie data={sexStatsTEProcessed.pieData} count={statsTE?.data?.total} />
            </div>

            {/* Pie chart: Top 4 nationalities + Others (Terrain) - MEMOIZED */}
            {/* Nationality Stats Terrain Employees */}
            <div className="h-[450px]  border border-gray-500 rounded-lg shadow-md overflow-auto bg-white">
              <Card direction={'row'} items={statsNatTEProcessed.cardItems} />

              <BasicPie
                data={statsNatTEProcessed.pieData}
                count={statsNatTEProcessed.total}
                legendDirection={'row'}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ========================================
          CHARTS SECTION
          Bar charts and line charts for detailed analytics
          ======================================== */}
      <div className="grid grid-cols-1 sm:grid-cols-3  mt-5 gap-3">
        {/* Bar chart: Enterprise distribution by department */}
        {ENDRStatsProcessed && (
          <MuiBarCharts
            data={ENDRStatsProcessed.data}
            xLabel={ENDRStatsProcessed.xLabel}
            title={t('title_repartition_dep')}
          />
        )}

        {/* Line chart: Evolution of permits by year */}
        {statsPermsProcessed && (
          <MuiLineChart
            xAxis={statsPermsProcessed.xAxis}
            data={statsPermsProcessed.data}
            title={t('title_evolution')}
          />
        )}

        {/* Bar chart: Employee distribution by salary range */}
        {statsSalaryProcessed && (
          <MuiBarCharts
            data={statsSalaryProcessed.data}
            xLabel={statsSalaryProcessed.xLabel}
            title={t('title_repartition_tra')}
          />
        )}

        {/* Bar chart: Top 10 nationalities */}
        {statsNatTop10Processed ? (
          <MuiBarCharts
            data={statsNatTop10Processed.data}
            xLabel={statsNatTop10Processed.xLabel}
            title={t('title_top_ten')}
          />
        ) : (
          <p>{t('no_data')}</p>
        )}
      </div>
    </div>
  );
};

export default TabContent2;
